import { apiUrl } from '@/core/api/base'

export type PortalBlattStatus = 'offen' | 'gespeichert' | 'unterschrieben'

export type PortalSession = {
  token: string
  sprache: string | null
  blaetter: { blatt: string; status: PortalBlattStatus }[]
}

export type PortalInhalt = { sprache: string; titel: string; text: string }

export type PortalBlatt = {
  blatt: string
  daten: Record<string, unknown>
  status: PortalBlattStatus
  unterschrieben_am: string | null
  inhalt?: PortalInhalt | null
  inhalt_de?: PortalInhalt | null
  dokument_id?: string | null
  gegengezeichnet_am?: string | null
}

export class PortalError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    public readonly body: unknown,
  ) {
    super(`portal ${status} on ${path}`)
    this.name = 'PortalError'
  }
}

export function portalErrorDetail(error: unknown): string {
  if (!(error instanceof PortalError)) return ''
  const body = error.body
  if (typeof body === 'string') return body
  if (body && typeof body === 'object') {
    const detail = (body as { detail?: unknown }).detail
    if (typeof detail === 'string') return detail
  }
  return ''
}

const PDF_FEHLT = 'pdf_fehlt'

export function detailNamesToken(detail: string, token: string): boolean {
  return detail === token || detail.startsWith(`${token}:`)
}

export type PortalSessionId = number

export type OwnPortalSession = { id: PortalSessionId; token: string }

type LiveSession = { id: PortalSessionId; token: string; requests: Map<AbortController, boolean> }

let live: LiveSession | null = null
const savingSessions = new Map<PortalSessionId, LiveSession>()
let lastSessionId = 0
export class PortalSessionEnded extends Error {
  constructor(public readonly path: string) {
    super(`portal session ended before ${path} completed`)
    this.name = 'PortalSessionEnded'
  }
}

function endLiveSession(): void {
  const ending = live
  live = null
  if (!ending) return
  abortSession(ending, savingSessions.has(ending.id))
}

function abortSession(ending: LiveSession, keepDrafts = false): void {
  for (const [controller, draft] of ending.requests) {
    if (keepDrafts && draft) continue
    controller.abort()
    ending.requests.delete(controller)
  }
}

function sessionById(id: PortalSessionId | null, draft = false): LiveSession | null {
  return id === null ? null : live?.id === id ? live : draft ? savingSessions.get(id) ?? null : null
}

export function retainPortalSessionForSave(id: PortalSessionId): void {
  const session = sessionById(id, true)
  if (session) {
    savingSessions.set(id, session)
    if (live === session) endLiveSession()
  }
}

export function startPortalSession(session: PortalSession): PortalSessionId {
  endLiveSession()
  lastSessionId += 1
  live = { id: lastSessionId, token: session.token, requests: new Map() }
  return lastSessionId
}

export function endPortalSession(sessionId: PortalSessionId | null): void {
  const session = sessionById(sessionId, true)
  if (!session) return
  savingSessions.delete(session.id)
  if (live === session) live = null
  abortSession(session)
}

export function currentPortalSession(): PortalSessionId | null {
  return live ? live.id : null
}

function authHeaders(bearer: string | null): Record<string, string> {
  return bearer ? { Authorization: `Bearer ${bearer}` } : {}
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => '')
  if (!text) return undefined
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export const REQUEST_TIMEOUT_MS = 15 * 1000

export const COMMIT_TIMEOUT_MS = 60 * 1000

export class PortalTimeout extends Error {
  constructor(public readonly path: string) {
    super(`portal request timed out on ${path}`)
    this.name = 'PortalTimeout'
  }
}

type RequestAuth = { session: PortalSessionId | null; draft?: true } | { bearer: string }

type Method = 'GET' | 'POST' | 'PUT'

async function request<T>(
  method: Method,
  path: string,
  body: unknown,
  auth: RequestAuth,
  timeoutMs: number = REQUEST_TIMEOUT_MS,
  answer: 'json' | 'pdf' = 'json',
): Promise<T> {
  const requestId = crypto.randomUUID()
  for (let retry = false; ; retry = true) {
    let owner: LiveSession | null = null
    let bearer: string | null = null
    if ('session' in auth) {
      owner = sessionById(auth.session, auth.draft)
      if (!owner) throw new PortalSessionEnded(path)
      bearer = owner.token
    } else if ('bearer' in auth) {
      bearer = auth.bearer
    }
    const headers: Record<string, string> = {
      Accept: answer === 'pdf' ? 'application/pdf' : 'application/json',
      'X-Request-Id': retry ? `${requestId}-retry` : requestId,
      ...authHeaders(bearer),
    }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    const controller = new AbortController()
    owner?.requests.set(controller, 'session' in auth && auth.draft === true)
    let timer: ReturnType<typeof setTimeout> | undefined
    let timedOut = false
    const deadline = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        timedOut = true
        controller.abort()
        reject(new PortalTimeout(path))
      }, timeoutMs)
    })
    const ended = new Promise<never>((_, reject) => {
      controller.signal.addEventListener('abort', () => {
        if (!timedOut) reject(new PortalSessionEnded(path))
      })
    })
    const exchange = (async (): Promise<T> => {
      const response = await fetch(apiUrl(path).toString(), {
        method,
        credentials: 'omit',
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      })
      if (!response.ok) throw new PortalError(response.status, path, await readBody(response))
      if (response.status === 204) return undefined as T
      if (answer === 'pdf') return (await response.blob()) as T
      return (await readBody(response)) as T
    })()
    try {
      const result = await Promise.race([exchange, deadline, ended])
      if (owner && sessionById(owner.id, 'session' in auth && auth.draft) !== owner) throw new PortalSessionEnded(path)
      return result
    } catch (error) {
      if (owner && sessionById(owner.id, 'session' in auth && auth.draft) !== owner && !(error instanceof PortalSessionEnded)) throw new PortalSessionEnded(path)
      if (!retry && method !== 'POST' && error instanceof PortalTimeout) continue
      throw error
    } finally {
      clearTimeout(timer)
      owner?.requests.delete(controller)
    }
  }
}

function blattPath(blatt: string): string {
  return `/portal/vergabe/blatt/${encodeURIComponent(blatt)}`
}

export function isPortalSession(body: unknown): body is PortalSession {
  if (!body || typeof body !== 'object') return false
  const { token: t, sprache, blaetter } = body as Record<string, unknown>
  return (
    typeof t === 'string' &&
    t.length > 0 &&
    (sprache === null || sprache === undefined || typeof sprache === 'string') &&
    Array.isArray(blaetter)
  )
}

export function setSprache(sprache: string, session: PortalSessionId): Promise<void> {
  return request<void>('PUT', '/portal/vergabe/sprache', { sprache }, { session })
}

export function getBlatt(blatt: string, session: PortalSessionId): Promise<PortalBlatt> {
  return request<PortalBlatt>('GET', blattPath(blatt), undefined, { session })
}

export function getBlattWithToken(blatt: string, token: string): Promise<PortalBlatt> {
  return request<PortalBlatt>('GET', blattPath(blatt), undefined, { bearer: token })
}

export function getBlattPdf(blatt: string, session: PortalSessionId): Promise<Blob> {
  return request<Blob>('GET', `${blattPath(blatt)}/pdf`, undefined, { session }, REQUEST_TIMEOUT_MS, 'pdf')
}

export async function saveBlatt(
  blatt: string,
  daten: Record<string, unknown>,
  session: PortalSessionId,
): Promise<'gespeichert' | 'unterschrieben'> {
  try {
    await request<void>('PUT', blattPath(blatt), { daten }, { session, draft: true })
    return 'gespeichert'
  } catch (error) {
    if (error instanceof PortalError && error.status === 409) return 'unterschrieben'
    throw error
  }
}

export async function signBlatt(
  blatt: string,
  signaturPngBase64: string,
  session: PortalSessionId,
): Promise<'unterschrieben' | 'schon_unterschrieben'> {
  try {
    await request<void>(
      'POST',
      `${blattPath(blatt)}/unterschrift`,
      { signatur_png_base64: signaturPngBase64 },
      { session },
      COMMIT_TIMEOUT_MS,
    )
    return 'unterschrieben'
  } catch (error) {
    if (error instanceof PortalError && error.status === 409 && !detailNamesToken(portalErrorDetail(error), PDF_FEHLT)) {
      return 'schon_unterschrieben'
    }
    throw error
  }
}

export function pngBase64(dataUrl: string): string {
  const comma = dataUrl.indexOf(',')
  return comma === -1 ? dataUrl : dataUrl.slice(comma + 1)
}

export type AbschliessenResult = { ok: true } | { ok: false; fehlend: string[] }

function fehlendOf(body: unknown): string[] {
  if (!body || typeof body !== 'object') return []
  const list = (body as { fehlend?: unknown }).fehlend
  return Array.isArray(list) ? list.filter((item): item is string => typeof item === 'string') : []
}

export async function abschliessen(session: PortalSessionId): Promise<AbschliessenResult> {
  try {
    await request<void>('POST', '/portal/vergabe/abschliessen', undefined, { session }, COMMIT_TIMEOUT_MS)
    return { ok: true }
  } catch (error) {
    if (error instanceof PortalError && error.status === 409) return { ok: false, fehlend: fehlendOf(error.body) }
    throw error
  }
}

export async function logoutToken(token: string, sessionId: PortalSessionId | null = null): Promise<void> {
  endPortalSession(sessionId)
  try {
    await request<void>('POST', '/portal/vergabe/logout', undefined, { bearer: token })
  } catch {
    // The local session has ended already; the server's idle TTL covers the rest.
  }
}
